import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Table } from 'antd'
import { CaretDownFilled, CaretRightFilled } from '@ant-design/icons'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetAccountsSelectorVirtualized from 'src/widgets/Selectors/WidgetAccountsSelectorVirtualized'
import WidgetProjectMultiSelector from 'src/widgets/Multi-Selectors/WidgetProjectMultiSelector'
import { wtFormatMoney } from 'src/helpers/commons/utils'

const CwipClassificationProjectDetailReport = () => {
  const Base = new BaseListing('cwip_classification_project_detail_report')
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()

  const [accounts, setAccounts] = useState([])
  const [projects, setProjects] = useState([])

  const [accountId, setAccountId] = useState()
  const [accountName, setAccountName] = useState('')
  const [selectedProjects, setSelectedProjects] = useState([])

  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [summaryData, setSummaryData] = useState({})
  const [expandedRows, setExpandedRows] = useState([])
  const [summaryPdfPath, setSummaryPdfPath] = useState('')
  const [detailPdfPath, setDetailPdfPath] = useState('')

  const companyName = wtGetFromLocalStorage('company_name')
  const branches = wtGetFromLocalStorage('branches')
  const [dateRange, setDateRange] = useState([])

  const changeValues = (key, val, label) => {
    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'account_id') {
      setAccountId(val)
      if (val === undefined) {
        setAccountName('')
      } else {
        setAccountName(label[0])
      }
    }
  }

  useEffect(() => {
    Base.dropdownData(
      'accounts,projects',
      { ignore_last_level: false, enable_all_levels: true },
      [setAccounts, setProjects],
      1,
    )
  }, [])

  const summaryColumns = [
    {
      title: <span style={{ fontSize: '0.8rem', marginLeft: '5%' }}>Account Description</span>,
      dataIndex: 'name',
      key: 'name',
      width: '70%',
      onCell: (record) => ({
        onClick: () => handleExpand(record),
        style: { cursor: record.type === 'group' ? 'pointer' : 'default' },
      }),
      render: (text, record) => (
        <span style={getRowTextStyle(record, record.type === 'group')}>
          {record.type === 'item' ? `${record.sr}. ${text}` : text}
        </span>
      ),
    },
    {
      title: <span style={{ fontSize: '0.8rem' }}>Total Expenditure</span>,
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (value, record) => (
        <span style={getRowTextStyle(record)}>{wtFormatMoney(value, '', false, 2)}</span>
      ),
    },
  ]

  const getRowTextStyle = (record, clickable = false) => ({
    fontWeight: record.type === 'group' || record.type === 'total' ? 'bold' : 'normal',
    fontSize: record.type === 'total' ? '0.83rem' : '0.8rem',
    ...(clickable ? { cursor: 'pointer' } : {}),
  })

  const normalizedSummaryGroups = Object.entries(summaryData || {}).map(([classification, rows]) => {
    const childRows = (rows || []).map((row, index) => ({
      key: `${classification}-item-${index}`,
      sr: index + 1,
      type: 'item',
      ...row,
    }))

    const classificationTotal = childRows.reduce(
      (sum, row) => sum + Number(row.total_amount || 0),
      0,
    )

    return {
      key: `group-${classification}`,
      name: classification,
      total_amount: classificationTotal,
      type: 'group',
      hasItems: childRows.length > 0,
      children: [
        ...childRows,
        {
          key: `group-${classification}-total`,
          name: 'Classification Total',
          total_amount: classificationTotal,
          type: 'total',
        },
      ],
    }
  })

  const grandTotal = normalizedSummaryGroups.reduce(
    (sum, group) => sum + Number(group.total_amount || 0),
    0,
  )

  const handleExpand = (record) => {
    if (record.type !== 'group' || !record.hasItems) {
      return
    }

    setExpandedRows((prevExpandedRows) =>
      prevExpandedRows.includes(record.key)
        ? prevExpandedRows.filter((rowKey) => rowKey !== record.key)
        : [...prevExpandedRows, record.key],
    )
  }

  const expandIcon = ({ expanded, onExpand, record }) => {
    if (record.hasItems && record.type === 'group') {
      return (
        <span style={{ marginRight: '2%' }}>
          {expanded ? (
            <CaretDownFilled
              style={{ color: '#706f71', fontSize: 12 }}
              onClick={(e) => onExpand(record, e)}
            />
          ) : (
            <CaretRightFilled
              style={{ color: '#706f71', fontSize: 12 }}
              onClick={(e) => onExpand(record, e)}
            />
          )}
        </span>
      )
    }

    return <span style={{ marginRight: '3%' }}></span>
  }

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }

    const fromDate = dateRange[0]
    const toDate = dateRange[1]

    setIsLoading(true)
    setSummaryData({})
    setExpandedRows([])
    setSummaryPdfPath('')
    setDetailPdfPath('')
    wtApiCall(
      'reports/cwip_classification_project_detail_report',
      {
        branch_id: branchId,
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        account_id: accountId,
        project_ids: JSON.stringify(selectedProjects),
      },
      'get',
      (response) => {
        if (response?.success === false) {
          lzNotifyErrorMessageFromResult(response)
          setIsLoading(false)
          return
        }

        if (response !== null && response.data !== null) {
          const nextSummaryData = response.data?.summary_data || {}
          setSummaryData(nextSummaryData)
          setExpandedRows(
            Object.keys(nextSummaryData || {}).map((classification) => `group-${classification}`),
          )
          const nextSummaryPdfPath = response.data?.summary_pdf_path || ''
          const nextDetailPdfPath = response.data?.detail_pdf_path || ''
          setSummaryPdfPath(nextSummaryPdfPath)
          setDetailPdfPath(nextDetailPdfPath)
          if (!nextSummaryPdfPath || !nextDetailPdfPath) {
            lzNotifyErrorMessageFromResult(response?.message || 'No PDF found in response')
          }
        }

        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          title={
            <span style={{ fontSize: '22px', marginLeft: '26%' }}>
              {companyName + ' Project Detailed Report V2'}
            </span>
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} sm={12} md={5}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => setDateRange(value || [])}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={24} sm={24} md={8}>
              <label className="account_dropdown_label_class">Account: *</label>
              <WidgetAccountsSelectorVirtualized
                data={accounts}
                value={accountId}
                displayValue={accountName}
                height={35}
                onChange={(value, title) => changeValues('account_id', value, title)}
                reloadDropdownData={() => Base.dropdownData('accounts', {}, [setAccounts], 1)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <label>Project: </label>
              <WidgetProjectMultiSelector
                Projects={projects}
                value={selectedProjects}
                onChange={(values) => setSelectedProjects(values)}
              />
            </Col>
            <Col xs={24} sm={12} md={3}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={24} sm={12} md={2}>
              <Button
                style={{ width: '100%', height: 35 }}
                type="primary"
                htmlType="submit"
                disabled={!!isLoading}
                loading={!!isLoading}
              >
                Search
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>

      {normalizedSummaryGroups.length > 0 && (
        <div style={{ overflow: 'auto', margin: '32px auto 0', width: '80%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <Button
              type="primary"
              style={{ backgroundColor: '#1d4ed8', borderColor: '#1d4ed8' }}
              disabled={!summaryPdfPath || !!isLoading}
              onClick={() => window.open(summaryPdfPath, '_blank', 'noopener,noreferrer')}
            >
              Summary PDF
            </Button>
            <Button
              type="primary"
              style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
              disabled={!detailPdfPath || !!isLoading}
              onClick={() => window.open(detailPdfPath, '_blank', 'noopener,noreferrer')}
            >
              Details PDF
            </Button>
          </div>
          <Table
            title={() => <span style={{ fontSize: '1rem', fontWeight: 700 }}>Summary Data</span>}
            columns={summaryColumns}
            dataSource={normalizedSummaryGroups}
            pagination={false}
            expandable={{ expandedRowKeys: expandedRows, expandIcon: expandIcon }}
            scroll={{ x: 'max-content' }}
            bordered
            size="small"
            loading={isLoading}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} align="right">
                  <strong>Grand Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <strong>{wtFormatMoney(grandTotal, '', false, 2)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>
      )}
    </div>
  )
}

export default CwipClassificationProjectDetailReport
